import React, { useMemo, useCallback, useSyncExternalStore } from 'react';
import { subscribe, getSnapshot } from './store';
import { verseKey, verseParts, resolveRange, mapRange, buildRenderItems, runStyle } from './anchors';
import { registerVerse } from './dom';

const EMPTY = [];

// The store's current marks; re-renders the caller when they change.
export function useAnnotationSnapshot() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// Inline style for a run of text covered by marks. Colours come from CSS
// variables so each theme can pick shades that read on its own background.
function markStyle({ highlight, line }) {
  const style = {};
  if (highlight) style.backgroundColor = `var(--anno-${highlight}-bg)`;
  if (line) {
    style.textDecorationLine = 'underline';
    style.textDecorationStyle = line.style === 'wavy' ? 'wavy' : line.style === 'dotted' ? 'dotted' : 'solid';
    style.textDecorationColor = `var(--anno-${line.color}-line)`;
  }
  return style;
}

// ── One verse's text with the reader's highlights, underlines and notes ──────
// `text` is the verse's plain text; `marked`, when Refs is on, is the same
// verse with "[a]" markers, drawn through `renderMarker(marker, key)`. Marks are
// stored against the plain text and mapped onto what is drawn, so they land on
// the same words with or without markers. A mark whose words are no longer
// there (the text was corrected since) is left out rather than drawn on the
// wrong ones.
export function AnnotatedVerse({ book, chapter, verse, lang, text, marked = null, renderMarker }) {
  const snap = useAnnotationSnapshot();
  const recs = snap.byVerse.get(verseKey(book, chapter, verse, lang)) || EMPTY;
  const plain = text || '';
  const { parts, display } = useMemo(() => verseParts(plain, marked), [plain, marked]);

  const items = useMemo(() => {
    const marks = [];
    for (const rec of recs) {
      const inPlain = resolveRange(plain, rec);
      const shown = inPlain && mapRange(plain, display, inPlain[0], inPlain[1]);
      if (shown) marks.push({ rec, start: shown[0], end: shown[1] });
    }
    return buildRenderItems(parts, marks, snap.noteEnds);
  }, [recs, plain, display, parts, snap.noteEnds]);

  const ref = useCallback(el => {
    registerVerse(el, { book, chapter, verse, lang, plain, display });
  }, [book, chapter, verse, lang, plain, display]);

  return (
    <span className="anno-verse" ref={ref}>
      {items.map((item, i) => {
        if (item.kind === 'note') {
          // Empty on purpose: the icon is drawn by CSS, so it adds no text
          // that a selection or the offset count could pick up.
          return (
            <span key={i} className="anno-note-icon anno-skip" data-anno-group={item.rec.group_id}
              role="button" aria-label="Note" title={item.rec.note} />
          );
        }
        const content = item.kind === 'marker'
          ? (renderMarker ? renderMarker(item.marker, i) : null)
          : item.text;
        if (!item.marks.length) return <React.Fragment key={i}>{content}</React.Fragment>;
        const look = runStyle(item.marks);
        return (
          <span key={i} className="anno-mark" data-anno-group={look.top.group_id} style={markStyle(look)}>
            {content}
          </span>
        );
      })}
    </span>
  );
}
